import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { CALENDLY, STATS } from "@/data";
import LeadFormCard from "@/components/LeadFormCard";
import PartnerBadges from "@/components/PartnerBadges";

const line = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.2 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

function MaskLine({ children, i }) {
  return (
    <span className="text-line-mask">
      <motion.span variants={line} custom={i} initial="hidden" animate="visible" className="block">
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -100]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pt-12 pb-16 lg:min-h-[calc(100vh-140px)]">
      {/* Aurora background */}
      <motion.div style={{ y: y1 }} className="aurora-blob top-[-6rem] left-[-4rem] h-[24rem] w-[24rem] bg-[#F48220]" />
      <motion.div style={{ y: y2 }} className="aurora-blob top-[6rem] right-[-8rem] h-[30rem] w-[30rem] bg-[#3A53A3]" />
      <motion.div style={{ y: y1 }} className="aurora-blob bottom-[-8rem] left-[25%] h-[22rem] w-[22rem] bg-[#E70D71]" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
        {/* Left: pitch */}
        <div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 mb-6"
          >
            <span className="h-2 w-2 rounded-full bg-[#F48220]" />
            <span className="font-body text-xs font-bold uppercase tracking-[0.25em] text-black/60">
              Growth Audit for D2C Brands
            </span>
          </motion.div>

          <h1 className="font-display font-extrabold tracking-tighter leading-[0.92] text-[clamp(2.4rem,5.5vw,5rem)] max-w-[15ch]">
            <MaskLine i={0}>Your brand doesn't</MaskLine>
            <MaskLine i={1}>need more ads.</MaskLine>
            <MaskLine i={2}><span className="text-[#E70D71]">It needs to find</span></MaskLine>
            <MaskLine i={3}><span className="text-[#F48220]">the leak.</span></MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-6 max-w-[46ch] font-body text-base md:text-lg leading-relaxed text-black/70"
          >
            A complete growth audit that exposes where you're losing money across
            performance marketing, creative, landing pages and retention. Just numbers,
            problems and what to fix.
          </motion.p>

          <motion.a
            href={CALENDLY}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-secondary"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-8 inline-flex items-center gap-2 rounded-full border border-black/15 px-7 py-3.5 font-body font-semibold text-black hover:bg-black hover:text-white transition-colors duration-300"
          >
            Prefer to talk? Book a 1-on-1 Call
          </motion.a>

          {/* Compact stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2 }}
            className="mt-12 grid grid-cols-4 gap-6"
          >
            {STATS.map((s) => (
              <div key={s.label}>
                <div className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-[#3A53A3]">{s.value}</div>
                <div className="mt-1 font-body text-[11px] md:text-xs text-black/55 leading-tight">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right: lead form (above the fold) */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <LeadFormCard />
          <PartnerBadges />
        </motion.div>
      </div>
    </section>
  );
}
